import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, alpha, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRightOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { PageUrl } from 'const/page-url';

// asset
import docService1 from 'assets/images/doc-service-1.webp';
import docService2 from 'assets/images/doc-service-2.webp';
import docService3 from 'assets/images/doc-service-3.webp';
import { SECTION_PY } from 'const/section';

// ==============================|| CONVERSION SECTION ||============================== //
//
// トップページ中盤に置くコンバージョン導線。
// 入力フォームをここに置くと縦に長くなってしまうため、バナーだけに留め、
// 実際の入力は専用ページ(/document-request)へ送る構成にしている。

const primaryColor = '#2aa1af';
const accentRed = '#e2574c';

type Card = {
    /** 重ねて表示する画像。1枚目が手前に来る */
    images: string[];
    /** 画像の見せ方。A4縦の資料を並べる場合のみ true にする */
    portrait: boolean;
    lead: ReactNode;
    label: string;
    to: string;
};

// CTAは資料請求の一本に絞っている。相談・問い合わせの導線を並べると
// 「まず何をすればよいか」が分かれてしまい、どちらも押されにくくなるため。
const card: Card = {
    images: [docService1, docService2, docService3],
    portrait: false,
    lead: (
        <>
            OHANA AI が<Box component="span" sx={{ color: accentRed }}>3分</Box>で分かる！
        </>
    ),
    label: '資料を受け取る',
    to: PageUrl.DocumentRequest.urlWithSlash
};

const ConversionSection = () => {
    const renderImages = (card: Card) => (
        <Box
            sx={{
                position: 'relative',
                height: { xs: 150, md: 190 },
                display: 'flex',
                justifyContent: 'center',
                mb: 2.5
            }}
        >
            {card.images.map((image, index) => (
                <Box
                    key={image}
                    component="img"
                    src={image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    sx={{
                        position: 'absolute',
                        // 奥の画像ほど上にずらして、束ねて置いたように見せる
                        bottom: `${index * 10}px`,
                        left: `calc(50% + ${index * (card.portrait ? 34 : 26)}px)`,
                        transform: 'translateX(-50%)',
                        height: card.portrait ? { xs: 130, md: 170 } : 'auto',
                        width: card.portrait ? 'auto' : { xs: 200, md: 260 },
                        borderRadius: '6px',
                        bgcolor: '#fff',
                        border: '1px solid #e0e6e6',
                        boxShadow: `0 10px 22px ${alpha('#000', 0.13)}`,
                        zIndex: card.images.length - index
                    }}
                />
            ))}
        </Box>
    );

    return (
        <Box
            component="section"
            id="document-request"
            sx={{
                py: SECTION_PY,
                background: `linear-gradient(135deg, ${primaryColor} 0%, #3bbcc9 100%)`,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(at 75% 20%, ${alpha('#fff', 0.2)} 0%, transparent 60%)`,
                    pointerEvents: 'none'
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                >
                    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
                        <Typography
                            component="p"
                            sx={{ color: alpha('#fff', 0.92), fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, mb: 1 }}
                        >
                            児童発達支援・放課後等デイサービス向け 記録・書類作成AI「OHANA AI」
                        </Typography>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '1.6rem', md: '2.4rem' },
                                color: '#fff',
                                lineHeight: 1.45,
                                textShadow: `0 2px 12px ${alpha('#000', 0.12)}`
                            }}
                        >
                            加算は取り切る、減算は出さない。
                        </Typography>
                        <Typography
                            component="p"
                            sx={{ color: alpha('#fff', 0.92), fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.05rem' }, mt: 2 }}
                        >
                            <Box component="span" sx={{ fontWeight: 900 }}>1ヶ月の無料トライアル</Box>つき。まずは資料でご確認ください。
                        </Typography>
                    </Box>
                </motion.div>

                <Grid container justifyContent="center">
                    <Grid item xs={12} sm={10} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.25 }}
                        >
                            <Box
                                sx={{
                                    p: { xs: 3, md: 4 },
                                    borderRadius: '20px',
                                    bgcolor: '#fff',
                                    boxShadow: `0 16px 40px ${alpha('#000', 0.16)}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                {renderImages(card)}

                                <Typography
                                    component="p"
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '1rem', md: '1.15rem' },
                                        color: '#1a1a1a',
                                        textAlign: 'center',
                                        mb: 2.5
                                    }}
                                >
                                    ＼ {card.lead} ／
                                </Typography>

                                <AnimateButton>
                                    <Button
                                        component={RouterLink}
                                        to={card.to}
                                        size="large"
                                        variant="contained"
                                        endIcon={<ArrowRightOutlined aria-hidden="true" />}
                                        sx={{
                                            px: { xs: 3.5, md: 5 },
                                            py: 1.7,
                                            borderRadius: '999px',
                                            fontSize: { xs: '0.98rem', md: '1.08rem' },
                                            fontWeight: 900,
                                            bgcolor: primaryColor,
                                            boxShadow: `0 10px 22px ${alpha(primaryColor, 0.35)}`
                                        }}
                                    >
                                        {card.label}
                                    </Button>
                                </AnimateButton>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default ConversionSection;
